import { cn } from '@/lib/utils';
import { Check, Globe, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';

export interface DisplayBankAgent {
    id: string;
    name: string;
    author: string;
    avatarColor: string;
    frequency: string;
    format: string;
    subscribers: string;
    blurb: string;
    isOwner?: boolean;
    isPublic: boolean;
}

interface BankCardProps {
    agent: DisplayBankAgent;
    isSubscribed: boolean;
    onToggle: () => void;
    onNavigate: () => void;
    onManage: () => void;
}

export function BankCard({
    agent,
    isSubscribed,
    onToggle,
    onNavigate,
    onManage
}: BankCardProps) {
    return (
        <div className="flex flex-col bg-background shadow-[0_1px_3px_rgba(0,0,0,.04)] p-5 border border-[#ecebe6] dark:border-border rounded-md h-64">
            <div className="flex justify-between items-start gap-2 mb-6">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div
                        className="flex justify-center items-center rounded-lg size-9 font-semibold text-white text-base shrink-0"
                        style={{ backgroundColor: agent.avatarColor }}
                    >
                        {agent.name[0]}
                    </div>
                    <div className="">
                        <div className="font-medium text-sm truncate">
                            {agent.frequency} - {agent.format}
                        </div>
                        <div className="text-muted-foreground text-sm">
                            {agent.author}
                        </div>
                    </div>
                </div>
            </div>

            {isSubscribed ? (
                <button
                    className="mb-0 font-medium text-lg text-left hover:underline cursor-pointer line-clamp-2"
                    onClick={onNavigate}
                >
                    {agent.name}
                </button>
            ) : (
                <div className="mb-0 font-medium text-lg text-left line-clamp-2">
                    {agent.name}
                </div>
            )}

            <p className="flex-1 min-h-0 overflow-hidden text-muted-foreground text-sm line-clamp-3">
                {agent.blurb}
            </p>

            <div className="flex justify-between items-center gap-2 mt-4 pt-3 border-border border-t">
                <div className="flex items-center gap-3 min-w-0">
                    <span
                        className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 border rounded-full font-medium text-[12px] shrink-0',
                            agent.isPublic
                                ? 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-950 dark:border-blue-900 dark:text-blue-200'
                                : 'bg-muted/50 border-border text-muted-foreground'
                        )}
                    >
                        {agent.isPublic ? (
                            <Globe className="size-3" />
                        ) : (
                            <Lock className="size-3" strokeWidth={2.5} />
                        )}
                        {agent.isPublic ? 'Public' : 'Private'}
                    </span>
                    <div className="text-muted-foreground text-sm truncate">
                        {agent.subscribers} subscribers
                    </div>
                </div>
                {agent.isOwner ? (
                    <Button
                        variant="tertiary"
                        size="default"
                        className="text-sm"
                        onClick={onManage}
                    >
                        Manage
                    </Button>
                ) : isSubscribed ? (
                    <Button
                        variant="tertiary"
                        size="default"
                        className="text-sm"
                        onClick={onToggle}
                    >
                        <Check />
                        Subscribed
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        size="default"
                        className="text-sm"
                        onClick={onToggle}
                    >
                        Subscribe
                    </Button>
                )}
            </div>
        </div>
    );
}
